/* eslint-disable max-len */
// =============================================================================
// XUẤT "Bản đăng ký tham gia Giải thưởng Khuê Văn Các" (.docx) theo MÔ HÌNH 2026.
// Cách làm: nạp template tối giản KVC_Ho_so.docx (chỉ có 1 placeholder {~~content}),
// dựng TOÀN BỘ nội dung hồ sơ thành 1 chuỗi HTML rồi inject qua docxtemplater +
// html-module. Mọi JSON.parse đều được bọc try/catch, dữ liệu thiếu/legacy không
// làm vỡ file.
// =============================================================================
import dayjs from "dayjs";
import PizZip from "pizzip";
import PizZipUtils from "pizzip/utils/index.js";
import Docxtemplater from "docxtemplater";

import { saveAs } from "file-saver";

import sample from "../assets/docs/KVC_Ho_so.docx";
import HTMLModule from "./docxtemplater-html-module";

import {
  FORM_CONSTANTS,
  REPORTS_CONSTANTS,
  RESEARCH_CONSTANTS,
  MONOGRAPHS_CONSTANTS,
  RESEARCHPROJECTS_CONSTANTS,
} from "../constants";
import { KEYS, CATEGORY } from "../constants/enums";
import {
  KVC2026_STATE_REWARD_OPTIONS,
  KVC2026_TYPICAL_WORK_FIELDS,
  KVC2026_COMMITMENT_FIELDS,
  composeTypicalWorkHtml,
} from "../constants/kvc2026.config";
import { resolveQcvLabel, isDomesticQcvCode } from "./qcvScoreOptions";

// Tra nhãn "Phân loại" cho 1 giá trị đã lưu, DÙNG CHUNG cho bản ghi mới (code,
// vd "1.1-a"/"2.1") lẫn bản ghi cũ (key cũ, vd "q1"/"domestic"). Mục suy ra từ
// chính code (phần trước dấu "-", hoặc nguyên code nếu không có dấu "-").
const findKvcLabel = (scoreInformationList, fallbackOptions, value) =>
  resolveQcvLabel(scoreInformationList, String(value || "").split("-")[0], fallbackOptions, value, "");

const loadFile = (url, callback) => PizZipUtils.getBinaryContent(url, callback);

// --- Helpers an toàn -------------------------------------------------------
const parseJson = (value, fallback = []) => {
  try {
    const parsed = JSON.parse(value || "null");
    return parsed == null ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
};

const esc = (value) =>
  String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const findTitle = (options, value) =>
  (options || []).find((o) => o.value === value)?.title || "";

// Ô "minh chứng": nếu là link DOI/ngoài thì dùng nguyên, nếu là file upload thì
// ghép domain. Trả về HTML <a> đã an toàn (href được escape).
const previewCell = (domain, value) => {
  if (!value) return "";
  const href = `${domain || ""}${value}`;
  return `<a href="${esc(href)}">Minh chứng</a>`;
};

// --- Dựng bảng -------------------------------------------------------------
const TABLE_STYLE =
  "width:100%;border-collapse:collapse;margin:4px 0 14px;font-size:13px;";
const TH = "border:1px solid #000;padding:4px 6px;background:#d9d9d9;text-align:left;font-weight:bold;";
const TD = "border:1px solid #000;padding:4px 6px;vertical-align:top;";

// rows: mảng các mảng cell (đã là HTML, KHÔNG tự escape ở đây).
const buildTable = (headers, rows) => {
  if (!rows || rows.length === 0) {
    return '<p style="margin:2px 0 12px;font-style:italic;">Không có.</p>';
  }
  const head = headers.map((h) => `<th style="${TH}">${h}</th>`).join("");
  const body = rows
    .map((r) => `<tr>${r.map((c) => `<td style="${TD}">${c}</td>`).join("")}</tr>`)
    .join("");
  return `<table style="${TABLE_STYLE}"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
};

const sectionTitle = (text) =>
  `<p style="margin:16px 0 4px;font-size:15px;font-weight:bold;background:#c8c7cc;padding:4px 6px;">${esc(text)}</p>`;

const subTitle = (text) =>
  `<p style="margin:10px 0 2px;font-weight:bold;">${esc(text)}</p>`;

// --- Bảng thành tích khoa học (chung: STT / Tên / Phân loại / Vai trò / MC) ---
const ACH_HEADERS = ["STT", "Tên công trình", "Phân loại", "Vai trò", "Minh chứng"];

const exportProfile = (rawForm, scoreInformationList = []) => {
  const form = rawForm || {};
  const profile = form.profile || {};
  const domain = process.env.REACT_APP_DOMAIN;

  loadFile(sample, (error, content) => {
    if (error) throw error;

    const zip = new PizZip(content);
    const doc = new Docxtemplater(zip, {
      linebreaks: true,
      paragraphLoop: true,
      modules: [new HTMLModule({})],
    });

    // ================= I. THÔNG TIN CÁ NHÂN =================
    const infoRows = [
      ["Họ và tên", profile.name],
      ["Ngày sinh", profile.birth ? dayjs(profile.birth).format("DD/MM/YYYY") : ""],
      ["Giới tính", findTitle(FORM_CONSTANTS.GENDER_OPTIONS, profile.gender)],
      ["Dân tộc", profile.ethnic],
      ["Số điện thoại", profile.phone],
      ["Email", profile.email],
      ["Website/Facebook", profile.website],
      ["Đơn vị công tác", form.unit],
      ["Lĩnh vực", findTitle(FORM_CONSTANTS.FIELD_OPTIONS[CATEGORY.KVC], form.field)],
      ["Đơn vị đề cử", form.nominator],
      ["Trình độ học vấn", findTitle(FORM_CONSTANTS.ACADEMIC_LEVEL_OPTIONS, form.academicLevel)],
      ["Học hàm/chức danh khoa học", form.academicRank],
      ["Ngoại ngữ", form.languague],
      ["Đoàn thể chính trị", findTitle(FORM_CONSTANTS.POLITICAL_UNION_OPTIONS, form.politicalUnion) || form.politicalUnion],
      ["Năng khiếu", form.gifted],
      ["Link video giới thiệu", form.videoUrl],
    ];
    const infoTable = `<table style="${TABLE_STYLE}"><tbody>${infoRows
      .map(
        ([label, value]) =>
          `<tr><td style="${TD}width:35%;font-weight:bold;">${esc(label)}</td><td style="${TD}">${esc(value)}</td></tr>`
      )
      .join("")}</tbody></table>`;

    // ================= A. CÔNG TRÌNH TIÊU BIỂU =================
    const twContent = parseJson(form.typicalWorkContent, null);
    let typicalHtml = "";
    if (twContent && typeof twContent === "object" && !Array.isArray(twContent)) {
      typicalHtml = composeTypicalWorkHtml(twContent);
    } else {
      const legacy = parseJson(form.summary, null);
      if (Array.isArray(legacy)) typicalHtml = legacy.join("<br/>");
      else if (typeof form.summary === "string") typicalHtml = form.summary;
    }
    if (!typicalHtml) {
      typicalHtml = KVC2026_TYPICAL_WORK_FIELDS.map(
        (f) => `<p><b>${esc(f.label)}:</b> </p>`
      ).join("");
    }

    // File minh chứng công trình tiêu biểu / cam kết (từ previewFile JSON).
    const fileList = parseJson(form.previewFile, []);
    const fileArr = Array.isArray(fileList) ? fileList : [];
    const sumaryFile = fileArr.find((f) => typeof f === "string" && f.includes("sumaryFile"));
    const camKetFile = fileArr.find((f) => typeof f === "string" && f.includes("camKetFile"));

    let typicalSection = typicalHtml;
    if (sumaryFile) {
      typicalSection += `<p style="margin-top:6px;"><b>Bản công trình tiêu biểu (có xác nhận): </b>${previewCell(domain, sumaryFile)}</p>`;
    }

    // ================= B. THÀNH TÍCH KHOA HỌC =================
    const researchArr = Array.isArray(form.formScientificResearch) ? form.formScientificResearch : [];
    const reportArr = Array.isArray(form.formScientificReport) ? form.formScientificReport : [];
    const monographArr = Array.isArray(form.formMonograph) ? form.formMonograph : [];
    const projectArr = Array.isArray(form.formResearchProject) ? form.formResearchProject : [];

    const researchTypeOptions = [
      ...RESEARCH_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL,
      ...RESEARCH_CONSTANTS.TYPE_OPTIONS.DOMESTIC,
    ];
    const monographTypeOptions = [
      ...MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL,
      ...MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.DOMESTIC,
    ];

    const researchRows = (list) =>
      list.map((item, i) => {
        const r = item?.scientificResearch || {};
        const mc = r.link ? previewCell(null, r.link) : previewCell(domain, r.preview);
        return [
          `${i + 1}`,
          esc(r.title),
          esc(findKvcLabel(scoreInformationList, researchTypeOptions, r.type)),
          esc(findTitle(RESEARCH_CONSTANTS.ROLE_OPTIONS, r.role)),
          mc,
        ];
      });

    const reportRows = (list) =>
      list.map((item, i) => {
        const r = item?.scientificReport || {};
        const mc = r.link ? previewCell(null, r.link) : previewCell(domain, r.preview);
        return [
          `${i + 1}`,
          esc(r.title),
          esc(findKvcLabel(scoreInformationList, REPORTS_CONSTANTS.TYPE_OPTIONS.ALL, r.type)),
          esc(findTitle(REPORTS_CONSTANTS.ROLE_OPTIONS, r.role)),
          mc,
        ];
      });

    const monographRows = (list) =>
      list.map((item, i) => {
        const r = item?.monograph || {};
        return [
          `${i + 1}`,
          esc(r.title),
          esc(findKvcLabel(scoreInformationList, monographTypeOptions, r.type)),
          esc(findTitle(MONOGRAPHS_CONSTANTS.ROLE_OPTIONS, r.role)),
          previewCell(domain, r.link),
        ];
      });

    // Chia quốc tế / trong nước.
    const researchInternational = researchArr.filter(
      (item) => item?.scientificResearch?.filterType === KEYS.INTERNATIONAL
    );
    const researchDomestic = researchArr.filter(
      (item) => item?.scientificResearch?.filterType === KEYS.DOMESTIC
    );
    // Ưu tiên nhận diện code MỚI qua qcv_score_infos, fallback mảng type cũ.
    const isDomKvc = (value, ...mucPrefixes) => {
      for (const muc of mucPrefixes) {
        const r = isDomesticQcvCode(scoreInformationList, muc, value, "");
        if (r !== null) return r;
      }
      return null;
    };

    const reportInternational = reportArr.filter((item) => {
      const isDom = isDomKvc(item?.scientificReport?.type, "1.2");
      if (isDom !== null) return !isDom;
      return REPORTS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL.find((t) => t.value === item?.scientificReport?.type);
    });
    // Backend "report" không lưu filterType, DOMESTIC options rỗng -> báo cáo
    // trong nước = "không thuộc quốc tế".
    const reportDomestic = reportArr.filter((item) => {
      const isDom = isDomKvc(item?.scientificReport?.type, "1.2");
      if (isDom !== null) return isDom;
      return (
        item?.scientificReport?.filterType === KEYS.DOMESTIC ||
        (!item?.scientificReport?.filterType &&
          !REPORTS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL.find(
            (t) => t.value === item?.scientificReport?.type
          ))
      );
    });
    const monographInternational = monographArr.filter((item) => {
      const isDom = isDomKvc(item?.monograph?.type, "2.3", "1.3");
      if (isDom !== null) return !isDom;
      return MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL.find((t) => t.value === item?.monograph?.type);
    });
    const monographDomestic = monographArr.filter((item) => {
      const isDom = isDomKvc(item?.monograph?.type, "2.3", "1.3");
      if (isDom !== null) return isDom;
      return MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.DOMESTIC.find((t) => t.value === item?.monograph?.type);
    });

    const projectRows = projectArr.map((item, i) => {
      const r = item?.researchProject || {};
      return [
        `${i + 1}`,
        esc(r.title),
        esc(resolveQcvLabel(scoreInformationList, "2.4", RESEARCHPROJECTS_CONSTANTS.LEVEL_OPTIONS, r.level, "")),
        esc(findTitle(RESEARCHPROJECTS_CONSTANTS.TYPO_OPTIONS, r.type)),
        previewCell(domain, r.link),
      ];
    });

    // ================= C. KHEN THƯỞNG & HOẠT ĐỘNG CỘNG ĐỒNG =================
    const commArrRaw = parseJson(form.communicationActivity, []);
    const commArr = Array.isArray(commArrRaw) ? commArrRaw : [];
    const stateReward = commArr.filter((r) => (r?.rewardGroup || "state") === "state");
    const ministryReward = commArr.filter((r) => r?.rewardGroup === "ministry");

    const stateRows = stateReward.map((r, i) => [
      `${i + 1}`,
      esc(r?.otherAchievementContent),
      esc(findTitle(KVC2026_STATE_REWARD_OPTIONS, r?.otherAchievementType)),
      previewCell(domain, r?.otherAchievementPreviewFile),
    ]);
    const ministryRows = ministryReward.map((r, i) => [
      `${i + 1}`,
      esc(r?.ministryRewardContent),
      previewCell(domain, r?.ministryRewardPreviewFile),
    ]);

    const personalArrRaw = parseJson(form.personalAchievement, []);
    const personalArr = Array.isArray(personalArrRaw) ? personalArrRaw : [];
    const personalRows = personalArr.map((r, i) => [
      `${i + 1}`,
      esc(r?.personalAchievementContent),
      previewCell(domain, r?.personalAchievementPreviewFile),
    ]);

    const socialArrRaw = parseJson(form.socialActivity, []);
    const socialArr = Array.isArray(socialArrRaw) ? socialArrRaw : [];
    const socialRows = socialArr.map((r, i) => [
      `${i + 1}`,
      esc(r?.socialActivityContent),
      previewCell(domain, r?.socialActivityPreviewFile),
    ]);

    // ================= D. CAM KẾT =================
    const commitment = parseJson(form.commitment, {});
    const commitmentObj = commitment && typeof commitment === "object" && !Array.isArray(commitment) ? commitment : {};
    let commitmentHtml = KVC2026_COMMITMENT_FIELDS.map(
      (f) => `<p style="margin:6px 0;"><b>${esc(f.label)}</b><br/>${esc(commitmentObj[f.key])}</p>`
    ).join("");
    if (camKetFile) {
      commitmentHtml += `<p style="margin-top:6px;"><b>Bản cam kết của ứng viên: </b>${previewCell(domain, camKetFile)}</p>`;
    }

    // ================= GHÉP TOÀN BỘ HTML =================
    const html = [
      `<p style="text-align:center;font-weight:bold;margin:0;">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</p>`,
      `<p style="text-align:center;font-weight:bold;margin:0;">Độc lập – Tự do – Hạnh phúc</p>`,
      `<p style="text-align:center;margin:0;">------</p>`,
      `<h2 style="text-align:center;">BẢN ĐĂNG KÝ THAM GIA GIẢI THƯỞNG KHUÊ VĂN CÁC</h2>`,

      sectionTitle("I. THÔNG TIN CÁ NHÂN"),
      infoTable,

      sectionTitle("A. CÔNG TRÌNH TIÊU BIỂU"),
      typicalSection,

      sectionTitle("B. THÀNH TÍCH KHOA HỌC"),
      subTitle("1. Công trình công bố quốc tế"),
      subTitle("1.1. Bài báo khoa học đăng trên tạp chí quốc tế"),
      buildTable(ACH_HEADERS, researchRows(researchInternational)),
      subTitle("1.2. Bài báo in trong kỷ yếu hội thảo quốc tế"),
      buildTable(ACH_HEADERS, reportRows(reportInternational)),
      subTitle("1.3. Sách chuyên khảo, giáo trình, chương sách (NXB quốc tế)"),
      buildTable(ACH_HEADERS, monographRows(monographInternational)),
      subTitle("2. Công trình công bố trong nước"),
      subTitle("2.1. Bài báo khoa học đăng trên tạp chí trong nước"),
      buildTable(ACH_HEADERS, researchRows(researchDomestic)),
      subTitle("2.2. Báo cáo khoa học tại hội thảo/hội nghị cấp ngành/toàn quốc"),
      buildTable(ACH_HEADERS, reportRows(reportDomestic)),
      subTitle("2.3. Sách chuyên khảo, chương sách, giáo trình (NXB trong nước)"),
      buildTable(ACH_HEADERS, monographRows(monographDomestic)),
      subTitle("2.4. Chương trình, dự án, đề tài nghiên cứu khoa học đã nghiệm thu"),
      buildTable(ACH_HEADERS, projectRows),

      sectionTitle("C. THÀNH TÍCH KHEN THƯỞNG VÀ HOẠT ĐỘNG CỘNG ĐỒNG"),
      subTitle("1. Thành tích khen thưởng"),
      subTitle("1.1. Được nhận hình thức khen thưởng cấp Nhà nước"),
      buildTable(["STT", "Nội dung khen thưởng", "Hình thức", "Minh chứng"], stateRows),
      subTitle("1.2. Được nhận Bằng khen Bộ, ban, ngành, tỉnh"),
      buildTable(["STT", "Nội dung khen thưởng", "Minh chứng"], ministryRows),
      subTitle("2. Hoạt động cộng đồng"),
      subTitle("2.1. Hoạt động chia sẻ tri thức, tập huấn, đào tạo,..."),
      buildTable(["STT", "Hình thức và nội dung thành tích", "Minh chứng"], personalRows),
      subTitle("2.2. Hoạt động tình nguyện vì cộng đồng"),
      buildTable(["STT", "Hình thức và nội dung thành tích", "Minh chứng"], socialRows),

      sectionTitle("D. CAM KẾT CỦA ỨNG VIÊN"),
      commitmentHtml,

      `<p style="margin:16px 0 4px;">Tôi xin hoàn toàn chịu trách nhiệm trước pháp luật, Ban tổ chức Giải thưởng về tính chính xác và các thông tin đã khai trong hồ sơ đăng ký trực tuyến tham gia xét giải thưởng</p>`,
      `<p style="text-align:right;font-style:italic;margin:8px 0;">…………, ngày……tháng……năm 2026</p>`,

      `<table style="width:100%;margin-top:24px;border:none;"><tbody><tr>` +
        `<td style="width:50%;text-align:center;border:none;"><b>XÁC NHẬN CỦA ĐƠN VỊ</b></td>` +
        `<td style="width:50%;text-align:center;border:none;"><b>NGƯỜI KHAI</b><br/><i>(Ký, ghi rõ họ tên)</i><br/><br/><br/><br/>${esc(profile.name)}</td>` +
        `</tr></tbody></table>`,
    ].join("");

    doc.render({ content: html });

    const blob = doc.getZip().generate({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    saveAs(blob, "Ban_dang_ky_KVC.docx");
  });
};

export default exportProfile;
